import React from 'react';
import { motion } from 'framer-motion';
import { Email, Warning } from '@mui/icons-material';
import { Typography, Alert, Box, CardContent } from '@mui/material';
import { MuiButton, MuiCard, ACEcolors } from '@ace/ace-ui-web';
import AuthHeader from '../../../components/auth-header/AuthHeader';
import styles from '../registration.module.css';

interface AdminSuccessScreenProps {
    email: string;
    phone?: string | null;
    navigate: (path: string) => void;
}

/**
 * Formats a phone number for display as (XXX) XXX-XXXX.
 * Accepts raw digits, dashed strings, or E.164 (e.g. +15551234567).
 * Returns null when fewer than 10 digits are available.
 */
const formatPhoneForDisplay = (value: string): string | null => {
    const digits = value.replaceAll(/\D/g, '');
    // Strip leading US country code if present (E.164: +1XXXXXXXXXX)
    const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (local.length !== 10) return null;
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
};

const AdminSuccessScreen: React.FC<AdminSuccessScreenProps> = ({ email, phone, navigate }) => {
    const formattedPhone = phone?.trim() ? formatPhoneForDisplay(phone) : null;
    const hasPhone = Boolean(formattedPhone);

    return (
        <div className={styles.appContainer}>
            <div className={styles.contentWrapper}>
                <AuthHeader className={styles.header} />

                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    <MuiCard borderRadius={2} elevation={5} padding={1} variant="elevation">
                        <CardContent>
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                                >
                                    <Email sx={{ fontSize: 64, color: ACEcolors.primary.default, mb: 2 }} />
                                </motion.div>

                                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    {hasPhone ? 'Check Your Email & Phone' : 'Check Your Email'}
                                </Typography>

                                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                                    Activation link sent to:
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 'bold', mb: hasPhone ? 1 : 3 }}
                                    aria-label={`Email address ${email}`}
                                >
                                    {email}
                                </Typography>

                                {hasPhone && (
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 'bold', mb: 3 }}
                                        aria-label={`Phone number ${formattedPhone}`}
                                    >
                                        {formattedPhone}
                                    </Typography>
                                )}

                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                    {hasPhone
                                        ? 'Please click the link in the email or text message to activate your account.'
                                        : 'Please click the link in the email to activate your account.'}
                                </Typography>

                                <Alert severity="warning" icon={<Warning />} sx={{ mt: 3, textAlign: 'left' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        This link expires in 20 minutes.
                                    </Typography>
                                </Alert>

                                <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Did not receive the email? Check your spam folder or contact your administrator.
                                    </Typography>
                                </Box>

                                <Box sx={{ mt: 4 }}>
                                    <MuiButton
                                        variant="outlined"
                                        size="large"
                                        onClick={() => navigate('/login')}
                                        sx={{ width: '100%' }}
                                    >
                                        Back to Login
                                    </MuiButton>
                                </Box>
                            </Box>
                        </CardContent>
                    </MuiCard>
                </motion.div>
            </div>
        </div>
    );
};

export default AdminSuccessScreen;